import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/integrations/supabase/client'
import { useState } from 'react'
import { Calendar, MapPin, QrCode, Globe, Loader2 } from 'lucide-react'
import { PortalPageHeader } from './ui/PortalPageHeader'
import { PortalEmptyState } from './ui/PortalEmptyState'
import { PortalSkeletonList } from './ui/PortalSkeleton'

const STATUS_COLORS: Record<string, string> = {
  invitations_sent: 'bg-primary/8 text-primary',
  active:           'bg-success/15 text-success',
  completed:        'bg-muted text-muted-foreground',
}

const STATUS_LABELS: Record<string, string> = {
  invitations_sent: 'Invitation sent',
  active:           'Event live',
  completed:        'Completed',
}

export function PortalEventsView() {
  const qc = useQueryClient()
  const [attendanceCode, setAttendanceCode] = useState('')
  const [submitResult, setSubmitResult] = useState<any>(null)

  // Uses the portal_event_selections view, bridges journalist_profiles ↔ journalists via email
  const { data: events, isLoading } = useQuery({
    queryKey: ['portal-event-selections'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) return []
      const { data, error } = await (supabase
        .from('portal_event_selections' as any)
        .select('*')
        .eq('portal_journalist_id', user.id)
        .neq('status', 'overridden')
        .in('event_status', ['invitations_sent', 'active', 'completed'])
        .order('start_at') as any)
      if (error) throw error
      return (data || []) as any[]
    },
  })

  // Accreditation check for empty-state messaging
  const { data: hasAccreditations } = useQuery({
    queryKey: ['portal-has-accreditations'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) return false
      const { count } = await (supabase
        .from('journalist_accreditations' as any)
        .select('id', { count: 'exact', head: true })
        .eq('journalist_id', user.id)
        .eq('status', 'active') as any)
      return (count ?? 0) > 0
    },
  })

  const attendanceMutation = useMutation({
    mutationFn: async (code: string) => {
      const { data: { user } } = await supabase.auth.getUser()
      const result = await supabase.functions.invoke('submit-attendance-code', {
        body: { code, journalist_id: user?.id },
      })
      if (result.error) throw new Error(result.error.message)
      if (result.data?.error) throw new Error(result.data.error)
      return result.data
    },
    onSuccess: (data) => {
      setSubmitResult(data)
      setAttendanceCode('')
      qc.invalidateQueries({ queryKey: ['portal-event-selections'] })
    },
    onError: () => setSubmitResult(null),
  })

  const upcoming = (events || []).filter((e: any) =>
    ['invitations_sent', 'active'].includes(e.event_status)
  )
  const past = (events || []).filter((e: any) => e.event_status === 'completed')

  return (
    <div className="max-w-3xl mx-auto px-4 py-6 space-y-6">
      <PortalPageHeader title="Events" description="Events you have been invited to, and attendance check-in." />

      {/* Attendance code box */}
      <div className="bg-background border border-border rounded-xl p-5">
        <div className="flex items-center gap-2 mb-3">
          <QrCode size={16} className="text-accent" />
          <h2 className="text-[15px] font-semibold">Submit Attendance Code</h2>
        </div>
        <p className="text-[12.5px] text-muted-foreground text-justify mb-4">
          Enter the attendance code from your invitation email or the event venue to
          confirm your attendance and earn coverage credit.
        </p>
        <div className="flex gap-2">
          <input
            value={attendanceCode}
            onChange={e => setAttendanceCode(e.target.value.toUpperCase())}
            placeholder="e.g. EP-2026-001-NG"
            className="flex-1 px-3 py-2.5 border border-border rounded-lg text-[13px]
                       font-mono bg-background uppercase focus:outline-none
                       focus:border-success"
          />
          <button
            disabled={!attendanceCode.trim() || attendanceMutation.isPending}
            onClick={() => attendanceMutation.mutate(attendanceCode.trim())}
            className="px-4 py-2.5 bg-success text-success-foreground rounded-lg text-[13px]
                       font-medium hover:bg-success disabled:opacity-50 transition-colors"
          >
            {attendanceMutation.isPending
              ? <span className="flex items-center gap-1.5"><Loader2 size={12} className="animate-spin" /> Submitting…</span>
              : 'Submit'}
          </button>
        </div>

        {submitResult && (
          <div className="mt-3 p-3 bg-success/15 border border-success/20
                          rounded-lg text-[12.5px] text-success">
            ✓ Attendance recorded for <strong>{submitResult.event_name}</strong>.
            {' '}Credit: {submitResult.credit === 'full' ? 'Full' : 'Partial'}.
          </div>
        )}
        {attendanceMutation.isError && (
          <div className="mt-3 p-3 bg-destructive/12 border border-red-200
                          rounded-lg text-[12.5px] text-destructive">
            {(attendanceMutation.error as Error).message}
          </div>
        )}
      </div>

      {/* Upcoming events */}
      <section>
        <h2 className="text-[15px] font-semibold mb-3">Upcoming Events</h2>

        {isLoading ? (
          <PortalSkeletonList rows={3} />
        ) : upcoming.length === 0 ? (
          <PortalEmptyState
            icon={Calendar}
            message={!hasAccreditations
              ? 'No active accreditations. Events appear here once you are accredited to an organisation.'
              : 'No upcoming events at this time. Check back closer to scheduled events.'}
          />
        ) : (
          <div className="space-y-3">
            {upcoming.map((event: any) => (
              <EventCard key={event.id} event={event} />
            ))}
          </div>
        )}
      </section>

      {/* Past events */}
      {past.length > 0 && (
        <section>
          <h2 className="text-[15px] font-semibold mb-3 text-muted-foreground">Past Events</h2>
          <div className="space-y-2 opacity-70">
            {past.map((event: any) => (
              <EventCard key={event.id} event={event} />
            ))}
          </div>
        </section>
      )}
    </div>
  )
}

function EventCard({ event }: { event: any }) {
  const statusColor = STATUS_COLORS[event.event_status] ?? 'bg-muted text-muted-foreground'
  const statusLabel = STATUS_LABELS[event.event_status] ?? event.event_status

  return (
    <div className="bg-background border border-border rounded-xl p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 min-w-0">
          <div className="text-[10px] font-medium text-muted-foreground uppercase tracking-wide mb-0.5">
            {event.org_name}
          </div>
          <h3 className="text-[14px] font-semibold leading-snug">{event.event_name}</h3>
          <div className="flex flex-wrap items-center gap-3 mt-2 text-[11.5px] text-muted-foreground">
            <span className="flex items-center gap-1">
              <Calendar size={11} />
              {new Date(event.start_at).toLocaleDateString('en', {
                day: 'numeric', month: 'long', year: 'numeric',
              })}
            </span>
            {(event.venue_city || event.host_country) && (
              <span className="flex items-center gap-1">
                <MapPin size={11} />
                {[event.venue_city, event.host_country].filter(Boolean).join(', ')}
              </span>
            )}
          </div>
        </div>

        <div className="flex flex-col items-end gap-1.5 shrink-0">
          <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${statusColor}`}>
            {statusLabel}
          </span>
          <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${
            event.attendance_type === 'physical'
              ? 'bg-primary text-primary-foreground'
              : 'bg-primary/10 text-primary'
          }`}>
            {event.attendance_type === 'physical' ? '📍 Physical' : '🖥 Online'}
          </span>
        </div>
      </div>

      {event.event_status === 'active' && (
        <div className="mt-3 pt-3 border-t border-border text-[12px] text-success font-medium">
          This event is live, enter your attendance code above to confirm attendance.
        </div>
      )}
      {event.event_status === 'invitations_sent' && (
        <div className="mt-3 pt-3 border-t border-border text-[12px] text-muted-foreground">
          You are selected. Attendance codes will be sent closer to the event date.
        </div>
      )}
      {event.event_status === 'active' && event.attendance_type === 'online' && event.online_code && (
        <div className="mt-2 flex items-center gap-2">
          <Globe size={11} className="text-muted-foreground shrink-0" />
          <span className="text-[11.5px] text-muted-foreground">Online code:</span>
          <code className="text-[11.5px] font-mono bg-muted px-2 py-0.5 rounded">
            {event.online_code}
          </code>
        </div>
      )}
    </div>
  )
}
